import 'utils/date'
import 'localization'
import 'antd/dist/antd.less'
import '@aws-amplify/ui-react/styles.css'
import 'utils/smartlook'
import type { AppProps } from 'next/app'
import { QueryClientProvider } from 'react-query'
import queryClient from 'src/query-client'
import { ThemeProvider } from 'theme'
import 'utils/aws'
import { useSessionProvider } from 'api/aws/hooks'
import Loader from 'components/Loader'
import styled from 'styled-components'
import GlobalModals from 'modules/common/GlobalModals'
import { color } from 'theme/mixins'
import * as Sentry from '@sentry/react'
import ErrorBoundary from 'modules/common/ErrorBoundary'
import AppSyncConsumer from 'api/aws/consumer'
import { useEffect, useState } from 'react'
import appRoutes from 'modules/common/app-routes'
import { useRouter } from 'next/router'
import { useConnectWealthicaUser, useCurrentUser, useIntegrations, useSyncUserPlan } from 'api'
import { useZendesk } from 'modules/common/hooks'
import ReactGA from 'react-ga4'
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react'
import { AppProvider, useAppContext } from 'modules/common/appContext'
import AuthHeader from 'modules/auth/AuthHeader'
import { mapWealthicPlanToBlockSentry } from 'api/wealthica/helpers'
import { usePlanUpgradeChecking } from 'modules/billings/hooks'
import { useDebouncedCallback } from 'use-debounce'
import dayjs from 'dayjs'
import http, { AUTO_OPEN_IMPORT, REDIRECT_URL_KEY, REFERRAL_KEY } from 'api/http'
import { Hub } from 'aws-amplify'
import { setReferral } from 'api/users/services'

const FullScreenLoader = styled(Loader)`
	background: ${color(['primary', '200'])};
	height: 100vh;
`
const cpaRoutes = [appRoutes.clients.list(), appRoutes.organization()]
const regularRoutes = [
	appRoutes.dashboard(),
	appRoutes.integrations.list(),
	appRoutes.reports.list(),
	appRoutes.transactions.list(),
]

if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID) {
	ReactGA.initialize(process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID)
}

const LayoutWrapper = ({ Component, pageProps }: AppProps) => {
	const [allowedRender, setAllowedRender] = useState(false)
	const router = useRouter()
	const { wealthicaContext } = useAppContext()
	const { data: user, isCPAview, isFetched: isUserFetched } = useCurrentUser()
	const { mutateAsync: syncUserPlan } = useSyncUserPlan()
	const { mutateAsync: connectWealthicaUser } = useConnectWealthicaUser()

	useEffect(() => {
		if (user?.email && !user?.externalProvider?.type && wealthicaContext?.authUser?.email) {
			connectWealthicaUser(wealthicaContext.authUser.email)
		}
	}, [user?.email, user?.externalProvider?.type, wealthicaContext?.authUser?.email])

	useEffect(() => {
		if (user && wealthicaContext?.plan && user.externalProvider?.userId === wealthicaContext.authUser?.email) {
			const newUserPlan = mapWealthicPlanToBlockSentry(wealthicaContext.plan)
			const wealthicaDateFormatted = wealthicaContext.paidUntil
				? dayjs(wealthicaContext.paidUntil).format('YYYY-MM-DD')
				: undefined
			const bsDateFormatted = user.paidUntil ? dayjs(user.paidUntil).format('YYYY-MM-DD') : undefined

			if (user.currentPlan !== newUserPlan || wealthicaDateFormatted !== bsDateFormatted) {
				syncUserPlan({
					layer: newUserPlan,
					nextPaymentDate: wealthicaContext.paidUntil?.toISOString(),
				})
			}
		}
	}, [user?.externalProvider?.userId, wealthicaContext?.plan, wealthicaContext?.paidUntil])

	useEffect(() => {
		const allowRender = () => setAllowedRender(true)

		if (isUserFetched) {
			// redirect when CPA is trying to access regular user pages without "as client" view
			if (isCPAview && regularRoutes.includes(router.pathname)) {
				router.push(appRoutes.organization()).then(allowRender)
				return
			}

			// redirect regular if viewing CPA routes
			if (!user?.isCPA && cpaRoutes.includes(router.pathname)) {
				router.push(appRoutes.dashboard()).then(allowRender)
				return
			}

			allowRender()
		}
	}, [isUserFetched, router.pathname, user?.isCPA, isCPAview])

	return allowedRender ? <Component {...pageProps} /> : <FullScreenLoader />
}

const AccessWrapper = (props: AppProps) => {
	const { Component, pageProps } = props
	const { allowedRender, isOnboarding } = useSessionProvider()
	const { setIsAuthenticated } = useAppContext()
	const authenticator = useAuthenticator()
	useZendesk()
	usePlanUpgradeChecking()

	useEffect(() => {
		if (authenticator.authStatus === 'authenticated') setIsAuthenticated(true)
		if (authenticator.authStatus === 'unauthenticated') setIsAuthenticated(false)
	}, [authenticator.authStatus])

	// no other wrappers to avoid redundant gated requests
	if (isOnboarding) return allowedRender ? <Component {...pageProps} /> : <FullScreenLoader />

	return allowedRender ? <LayoutWrapper {...props} /> : <FullScreenLoader />
}

const MyApp = (props: AppProps) => {
	const { isWealthicaContext, isFetched, isAuthenticated } = useAppContext()
	const router = useRouter()
	const { data: integrations = [] } = useIntegrations()

	// show cookie consent banner for non Wealthica context
	const debouncedOnContextInitialed = useDebouncedCallback(debouncedIsWealthicaContext => {
		if (!debouncedIsWealthicaContext) {
			const cookieConsent = document.querySelector('.cky-consent-container') as any
			if (cookieConsent && !cookieConsent.classList.contains('cky-hide')) {
				cookieConsent.style.display = 'block'
			}
		}
	}, 2000)

	useEffect(() => {
		const onContinue = async () => {
			const redirectUrl = http.getStorageItem(REDIRECT_URL_KEY)

			if (redirectUrl) {
				http.forgetStorageItem(REDIRECT_URL_KEY)
				router.push(redirectUrl)
				return
			}

			if (isWealthicaContext && integrations.length) {
				await router.push(appRoutes.integrations.list())
				localStorage.setItem(AUTO_OPEN_IMPORT, 'true')
				return
			}

			router.push(appRoutes.dashboard())
		}

		Hub.listen('auth', data => {
			switch (data.payload.event) {
				case 'signIn': {
					const referral = http.getStorageItem(REFERRAL_KEY)

					if (referral) {
						setReferral(referral).then(() => {
							http.forgetStorageItem(REDIRECT_URL_KEY)
							onContinue()
						})
					} else {
						onContinue()
					}
					break
				}
				case 'signOut':
				case 'oAuthSignOut':
					break
				default:
					break
			}
		})
	}, [])

	useEffect(() => {
		debouncedOnContextInitialed(isWealthicaContext)
	}, [isWealthicaContext])

	return (
		<div style={(isWealthicaContext && !isAuthenticated) || !isFetched ? { opacity: 0 } : undefined}>
			<Authenticator
				socialProviders={isWealthicaContext ? undefined : ['google']}
				components={{
					SetupTOTP: {
						Header: AuthHeader,
					},
					ResetPassword: {
						Header: AuthHeader,
					},
					VerifyUser: {
						Header: AuthHeader,
					},
					ConfirmSignUp: {
						Header: AuthHeader,
					},
					SignUp: {
						Header: AuthHeader,
					},
					SignIn: {
						Header: AuthHeader,
					},
				}}
				formFields={{
					signIn: {
						username: {
							placeholder: 'Email',
							autocomplete: 'off',
						},
					},
					signUp: {
						username: {
							placeholder: 'Email',
							autocomplete: 'off',
						},
					},
				}}
			>
				<Sentry.ErrorBoundary fallback={ErrorBoundary}>
					<AppSyncConsumer>
						<GlobalModals.Provider>
							<AccessWrapper {...props} />
						</GlobalModals.Provider>
					</AppSyncConsumer>
				</Sentry.ErrorBoundary>
			</Authenticator>
		</div>
	)
}

export default (props: AppProps) => (
	<AppProvider>
		{({ isWealthicaContext }) => {
			if (typeof window !== 'undefined') {
				if (isWealthicaContext) {
					document.getElementsByTagName('body')[0].style.background = 'transparent'
					document.getElementsByTagName('html')[0].style.minHeight = 'inherit'
					document.getElementsByTagName('html')[0].style.height = 'auto'
				} else {
					document.getElementsByTagName('body')[0].style.background = '#f5f5f5'
				}

				zE('messenger:set', 'cookies', false)
			}
			return (
				<ThemeProvider>
					<QueryClientProvider client={queryClient}>
						<MyApp {...props} />
					</QueryClientProvider>
				</ThemeProvider>
			)
		}}
	</AppProvider>
)
